import { useState, useCallback, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { productQueries } from "@surgia/api/src/queryStore/admin/product";

interface UseCustomerViewProps {
  initialCustomerView: boolean | undefined; // Allow undefined to handle loading states
  productId: string;
  isDisabled?: boolean;
  onSuccess?: () => void;
  tenantId: string;
  parentProductId: string;
}

interface UseCustomerViewReturn {
  customerView: boolean;
  toggleCustomerView: () => void;
  isDisabled: boolean;
  isLoading: boolean;
}

export const useCustomerView = ({
  initialCustomerView,
  productId,
  isDisabled = false,
  onSuccess,
  tenantId,
  parentProductId,
}: UseCustomerViewProps): UseCustomerViewReturn => {
  const [customerView, setCustomerView] = useState(
    initialCustomerView ?? false
  );

  // Update customerView when initialCustomerView changes (e.g., when API data loads)
  useEffect(() => {
    if (initialCustomerView !== undefined) {
      setCustomerView(initialCustomerView);
    }
  }, [initialCustomerView]);
  const queryClient = useQueryClient();

  // Mutation for updating customer view using the correct API
  const updateCustomerViewMutation = useMutation({
    mutationFn: async ({
      productId,
      customerView,
    }: {
      productId: string;
      customerView: boolean;
    }) => {
      return productQueries.mutations
        .updateTenantProductCustomerView(productId)
        .mutationFn({ customerView });
    },
    onSuccess: () => {
      toast.success("Visibility updated successfully!");
      queryClient.invalidateQueries({
        queryKey: ["admin", "product", "variations", parentProductId, tenantId],
      });
      onSuccess?.();
    },
    onError: () => {
      toast.error("Failed to update visibility. Please try again.");
      // Revert the local state on error
      setCustomerView((prev) => !prev);
    },
  });

  const toggleCustomerView = useCallback(() => {
    if (isDisabled) {
      toast.error("Please fill all required fields first");
      return;
    }

    const newCustomerView = !customerView;
    setCustomerView(newCustomerView);

    updateCustomerViewMutation.mutate({
      productId,
      customerView: newCustomerView,
    });
  }, [customerView, productId, isDisabled, updateCustomerViewMutation]);

  return {
    customerView,
    toggleCustomerView,
    isDisabled,
    isLoading: updateCustomerViewMutation.isPending,
  };
};
